'use client';

import { useI18n } from '@/lib/i18n';

// 2026-05-09 build 1215 — Veronika asked for the same empty-archive
// frame she approved on /projects (Finished.jsx) to be reused on the
// empty states of /techniques, /inspiration and /inventory. Shared
// component to keep the styling consistent across all four pages.
// Caller passes the page-specific i18n strings (title, hint, CTA
// label, plus the action handler) and we render the bordered B1 zine
// frame.
//
// Build 1286 (2026-05-10) — Veronika: "And the text will only be
// 'add new'." Visible button label is now a fixed `t.addNew` so
// every empty-archive reads the same, regardless of which page
// it lives on. The page-specific `ctaLabel` prop still funnels
// into the button's aria-label so screen readers and Playwright
// queries get the more specific name (e.g. "Add technique").

const fontDisp = "var(--font-geist-sans), system-ui, -apple-system, sans-serif";
const fontSerifA = "'Fraunces', 'Times New Roman', serif";
const HALFTONE_A = {
  backgroundImage:
    'radial-gradient(circle, rgba(15,14,12,0.85) 0.9px, transparent 1.4px)',
  backgroundSize: '4px 4px',
};

export type EmptyArchiveProps = {
  title: string;
  hint: string;
  ctaLabel: string;
  onAction: () => void;
};

export default function EmptyArchive({ title, hint, ctaLabel, onAction }: EmptyArchiveProps) {
  const { t } = useI18n();
  // 2026-05-10 build 1285 — Veronika: "Make it only one square."
  // Was: cream halftone outer card containing a smaller blue
  // "+ ADD …" button inside its top half. Two visible squares
  // → confusing where to tap. Now the BLUE block IS the top
  // half of the card: full-width solid electric blue with the
  // big white "+" + label, click anywhere in that area to fire
  // onAction. The bottom half stays cream/halftone with the
  // "NO TECHNIQUES YET" headline + hint, separated by an ink
  // hairline. One square, one tap target.
  return (
    <div style={{ padding: '14px 14px 14px', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          border: '1px solid var(--ink)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 2026-05-10 build 1287 — Veronika: "Now make it without
            the full color." Same one-square layout, but the
            blue fill is gone — back to cream halftone with
            ink "+" + label, same paper as the bottom info half.
            The two halves visually rhyme; only the "+" / label
            signals that the top half is interactive. */}
        <button
          onClick={onAction}
          aria-label={ctaLabel}
          style={{
            position: 'relative',
            cursor: 'pointer',
            background: 'var(--paper-2)',
            border: 'none',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 20px 22px',
            color: 'var(--ink)',
            gap: 8,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {/* "+" sized down 2026-05-10 build 1289 — Veronika:
              "And much smaller +". 110→48. Padding tightened to
              keep the button proportions reasonable. */}
          <span
            style={{
              position: 'relative',
              fontFamily: fontDisp,
              fontWeight: 800,
              fontSize: 48,
              lineHeight: 0.85,
              letterSpacing: '-0.04em',
              color: 'var(--ink)',
            }}
          >
            +
          </span>
          <span
            style={{
              position: 'relative',
              fontFamily: fontDisp,
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              textAlign: 'center',
              lineHeight: 1.05,
              color: 'var(--ink)',
            }}
          >
            {t.addNew}
          </span>
        </button>

        <div
          style={{
            position: 'relative',
            background: 'var(--paper-2)',
            borderTop: '1px solid var(--ink)',
            padding: '14px 18px 16px',
            textAlign: 'center',
            overflow: 'hidden',
          }}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              ...HALFTONE_A,
              opacity: 0.18,
              mixBlendMode: 'multiply',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'relative',
              fontFamily: fontDisp,
              fontWeight: 800,
              fontSize: 26,
              lineHeight: 0.95,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
            }}
          >
            {title}
          </div>
          <div
            style={{
              position: 'relative',
              fontFamily: fontSerifA,
              fontStyle: 'italic',
              fontSize: 13,
              color: 'var(--ink-2)',
              marginTop: 8,
              lineHeight: 1.4,
              maxWidth: 280,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            {hint}
          </div>
        </div>
      </div>
    </div>
  );
}
